import { useState, useCallback, useEffect, useRef } from 'react'

// Dentro de la app nativa no hay Web Bluetooth: los comandos al sensor viajan
// por el puente al caparazón (que habla BLE con react-native-ble-plx).
const IN_NATIVE_SHELL = typeof window !== 'undefined' && (window.__MANABI_SHELL__ || window.__VITALCORE_SHELL__ || !!window.ReactNativeWebView)

// ── PS-FTP service — FEEE (Polar File System) ─────────────────────────────────
const PSFTP_SERVICE = '0000feee-0000-1000-8000-00805f9b34fb'
// FB005C51: notify — device sends its MTU value here after connect
const PSFTP_MTU_CH  = 'fb005c51-02e7-f387-1cad-8acd2d8df0c8'
// FB005C52: notify — device→host data (response frames)
const PSFTP_D2H_CH  = 'fb005c52-02e7-f387-1cad-8acd2d8df0c8'
// FB005C53: write  — host→device data (request frames)
const PSFTP_H2D_CH  = 'fb005c53-02e7-f387-1cad-8acd2d8df0c8'

// ── RFC76 framing constants ───────────────────────────────────────────────────
// Header byte layout:  [seqNo:4] [status:2] [next:1] [0:1]
//   Status extracted as  (byte & 0x06) >> 1
//   Status values: 0=ERROR, 1=LAST, 3=MORE
// Build constants by OR-ing status back into position 2:1:
const RFC76_LAST  = 0x02   // status=1 shifted to bits[2:1]: 0b00000010
const RFC76_MORE  = 0x06   // status=3 shifted to bits[2:1]: 0b00000110

function rfc76Status(byte) { return (byte & 0x06) >> 1 }

// ── RFC76 encoder — wraps any payload into BLE packets ───────────────────────
function rfc76Wrap(payload, mtu) {
  const maxChunk = mtu - 1   // 1 byte for RFC76 header
  const frames   = []
  let   seq = 0, off = 0
  while (off < payload.length) {
    const end   = Math.min(off + maxChunk, payload.length)
    const chunk = payload.slice(off, end)
    off = end
    const hdr   = (seq << 4) | (off >= payload.length ? RFC76_LAST : RFC76_MORE)
    const frame = new Uint8Array(1 + chunk.length)
    frame[0] = hdr; frame.set(chunk, 1)
    frames.push(frame)
    seq = (seq + 1) & 0x0F
  }
  return frames
}

// ── RFC60 message wrapper — 2-byte little-endian length prefix ────────────────
// Required before RFC76 framing. For requests (not queries), high-bit of byte[1] = 0.
function rfc60Wrap(protoBytes) {
  const N   = protoBytes.length
  const buf = new Uint8Array(2 + N)
  buf[0] = N & 0xFF
  buf[1] = (N >> 8) & 0x7F   // high bit = 0 → request (not query)
  buf.set(protoBytes, 2)
  return buf
}

// ── Protobuf: encode PbPFtpOperation GET {path} ───────────────────────────────
function encodePbGet(path) {
  const pathBytes = new TextEncoder().encode(path)
  const buf       = new Uint8Array(4 + pathBytes.length)
  buf[0] = 0x08; buf[1] = 0x01         // field 1, varint GET=1
  buf[2] = 0x12; buf[3] = pathBytes.length
  buf.set(pathBytes, 4)
  return buf
}

// ── Protobuf: decode PbPFtpDirectory ─────────────────────────────────────────
function parseProtoDirectory(bytes) {
  const entries = []
  let i = 0

  function varint() {
    let v = 0, shift = 0
    while (i < bytes.length) {
      const b = bytes[i++]
      v |= (b & 0x7F) << shift
      if (!(b & 0x80)) break
      shift += 7
    }
    return v
  }

  while (i < bytes.length) {
    const tag     = bytes[i++]
    const fieldN  = tag >> 3
    const wire    = tag & 0x07

    if (fieldN === 1 && wire === 2) {                // repeated PbPFtpEntry
      const entLen = varint()
      const entEnd = i + entLen
      const entry  = { name: '', isDir: false, size: 0 }

      while (i < entEnd) {
        const etag = bytes[i++]
        const efn  = etag >> 3
        const ewt  = etag & 0x07
        if      (efn === 1 && ewt === 2) { const l = varint(); entry.name  = new TextDecoder().decode(bytes.slice(i, i + l)); i += l }
        else if (efn === 2 && ewt === 0) { entry.size  = varint() }
        else if (efn === 4 && ewt === 0) { entry.isDir = varint() !== 0 }
        else if (ewt === 0)              { varint() }
        else if (ewt === 2)              { const l = varint(); i += l }
        else                             { i = entEnd; break }
      }
      i = entEnd
      if (entry.name) entries.push(entry)
    } else if (wire === 0) { varint() }
    else if (wire === 2)   { const l = varint(); i += l }
    else break
  }
  return entries
}

// ── Utility ───────────────────────────────────────────────────────────────────
function concat(arrays) {
  const tot = arrays.reduce((s, a) => s + a.length, 0)
  const out = new Uint8Array(tot); let off = 0
  for (const a of arrays) { out.set(a, off); off += a.length }
  return out
}
function dvSlice(dv, from) {
  return new Uint8Array(dv.buffer, dv.byteOffset + from, dv.byteLength - from)
}

// ── Wait for device MTU notification on FB005C51 ──────────────────────────────
// Listener added BEFORE startNotifications to avoid the race where the device
// fires the notification before our handler is registered.
async function waitMtu(mtuCh, timeoutMs = 8000) {
  return new Promise(async (resolve) => {
    let done = false
    const timer = setTimeout(() => { if (!done) { done = true; resolve(20) } }, timeoutMs)

    function fn(e) {
      if (done) return
      done = true
      clearTimeout(timer)
      mtuCh.removeEventListener('characteristicvaluechanged', fn)
      const dv  = e.target.value
      // Value can be uint8 or uint16 LE depending on firmware
      const mtu = dv.byteLength >= 2 ? dv.getUint16(0, true) : dv.getUint8(0)
      resolve(mtu > 10 ? mtu : 20)
    }
    mtuCh.addEventListener('characteristicvaluechanged', fn)
    // Start notifications AFTER adding listener so we never miss the first event
    await mtuCh.startNotifications().catch(() => {})
  })
}

// ── Collect RFC76 response stream from D2H notifications ─────────────────────
// Strips RFC76 header from each frame, then strips the RFC60 2-byte prefix
// from the reassembled message to return raw protobuf bytes.
function collectResponse(d2hCh, timeoutMs = 25000) {
  return new Promise((resolve, reject) => {
    const chunks = []
    const timer  = setTimeout(() => {
      d2hCh.removeEventListener('characteristicvaluechanged', fn)
      reject(new Error(
        'El H10 no respondió al pedido de archivos. En computadora esto casi siempre es porque ' +
        'el sensor no está emparejado con el equipo: abrí Ajustes del sistema → Bluetooth, ' +
        'conectá "Polar H10" una vez (con el cinturón puesto), y volvé a intentar.'
      ))
    }, timeoutMs)

    function fn(e) {
      const dv     = e.target.value
      const hdr    = dv.getUint8(0)
      const status = rfc76Status(hdr)          // bits [2:1]

      if (status === 0) {                      // error frame
        clearTimeout(timer)
        d2hCh.removeEventListener('characteristicvaluechanged', fn)
        const code = dv.byteLength >= 3
          ? (dv.getUint8(1) | (dv.getUint8(2) << 8))  // LE uint16
          : -1
        const msgs = {
          103: 'El archivo o directorio no existe en el H10',
          5:   'El H10 requiere autenticación — el cinturón debe estar emparejado con este equipo',
        }
        reject(new Error(msgs[code] ?? `PS-FTP error ${code}`))
        return
      }

      if (dv.byteLength > 1) chunks.push(dvSlice(dv, 1))

      if (status === 1) {                      // LAST
        clearTimeout(timer)
        d2hCh.removeEventListener('characteristicvaluechanged', fn)
        const full = concat(chunks)
        // Strip RFC60 2-byte length prefix → raw protobuf
        resolve(full.length >= 2 ? full.slice(2) : full)
      }
      // status === 3 → MORE, keep collecting
    }
    d2hCh.addEventListener('characteristicvaluechanged', fn)
  })
}

// ── PS-FTP QUERY — comandos al sensor (id con bit alto 0x80 en el header) ─────
// Extraído del SDK oficial de Polar (BlePsFtpUtils.makeCompleteMessageStream):
//   QUERY  = [id&0xFF, ((id>>8)&0x7F)|0x80] + protobuf
// IDs (PbPFtpQuery): 14=empezar a grabar · 15=parar · 16=estado
const Q_START_RECORDING  = 14
const Q_STOP_RECORDING   = 15
const Q_RECORDING_STATUS = 16
// PbSampleType: 1=pulso (1/s) · 16=intervalos RR (pulso + HRV)
export const SAMPLE_HR = 1
export const SAMPLE_RR = 16

function encodeQueryMsg(id, protoBytes) {
  const N   = protoBytes ? protoBytes.length : 0
  const buf = new Uint8Array(2 + N)
  buf[0] = id & 0xFF
  buf[1] = ((id >> 8) & 0x7F) | 0x80   // bit alto = query
  if (protoBytes) buf.set(protoBytes, 2)
  return buf
}

// PbPFtpRequestStartRecordingParams: f1 sample_type · f2 recording_interval (PbDuration, seconds=f3) · f3 sample_data_identifier
function encodeStartRecordingParams(sampleType) {
  const id  = new TextEncoder().encode('VITALCORE')
  const out = [0x08, sampleType]
  if (sampleType === SAMPLE_HR) out.push(0x12, 0x02, 0x18, 0x01)   // interval {seconds:1} — RR es por evento, sin intervalo
  out.push(0x1A, id.length, ...id)
  return new Uint8Array(out)
}

// PbRequestRecordingStatusResult: f1 recording_on (bool) · f2 sample_data_identifier
export function parseRecordingStatus(b) {
  let on = false, id = '', i = 0
  while (i < b.length) {
    const tag = b[i++], f = tag >> 3, w = tag & 7
    if (f === 1 && w === 0) { on = b[i++] !== 0 }
    else if (w === 0) { while (b[i++] & 0x80) { /* varint */ } }
    else if (w === 2) {
      let l = 0, s = 0
      for (;;) { const x = b[i++]; l |= (x & 0x7F) << s; if (!(x & 0x80)) break; s += 7 }
      if (f === 2) id = new TextDecoder().decode(b.slice(i, i + l))
      i += l
    } else break
  }
  return { on, id }
}

async function psFtpQueryFixed(h2dCh, d2hCh, id, protoBytes, mtu) {
  const msg    = encodeQueryMsg(id, protoBytes)
  const frames = rfc76Wrap(msg, mtu)
  const responseP = collectResponse(d2hCh, 15000)
  for (const frame of frames) {
    try { await h2dCh.writeValueWithoutResponse(frame) }
    catch { await h2dCh.writeValueWithResponse(frame) }
    await new Promise(r => setTimeout(r, 20))
  }
  return responseP
}

async function psFtpGetFixed(h2dCh, d2hCh, path, mtu) {
  const proto  = encodePbGet(path)
  const msg    = rfc60Wrap(proto)
  const frames = rfc76Wrap(msg, mtu)

  // Set up response listener BEFORE writing so no notification is missed
  const responseP = collectResponse(d2hCh)

  for (const frame of frames) {
    // writeValueWithoutResponse avoids blocking the BLE receive queue while
    // waiting for L2CAP ACK, which can prevent D2H notifications on some firmware
    try {
      await h2dCh.writeValueWithoutResponse(frame)
    } catch {
      await h2dCh.writeValueWithResponse(frame)
    }
    // Small inter-frame gap so the H10 can process each chunk
    await new Promise(r => setTimeout(r, 20))
  }
  return responseP
}

// ── Parse SAMPLES.BPB (PbExerciseSamples) — el formato de la grabación interna ──
// f1 recording_interval (PbDuration: h=1 m=2 s=3 ms=4) · f2 heart_rate_samples
// (packed uint32) · f28 rr_samples (PbExerciseRRIntervals: f1 rr_intervals packed)
export function parsePbExerciseSamples(b, startMs) {
  let i = 0, intervalSec = 1
  const hr = [], rr = []
  const varint = () => { let v = 0, s = 0; for (;;) { const x = b[i++]; v |= (x & 0x7F) << s; if (!(x & 0x80)) return v >>> 0; s += 7 } }
  const skip = (w) => { if (w === 0) varint(); else if (w === 2) { const l = varint(); i += l } else if (w === 5) i += 4; else if (w === 1) i += 8 }
  while (i < b.length) {
    const tag = varint(), f = tag >> 3, w = tag & 7
    if (f === 1 && w === 2) {                       // recording_interval
      const end = i + varint()
      let h = 0, m = 0, s = 0, ms = 0
      while (i < end) {
        const t2 = varint(), f2 = t2 >> 3, w2 = t2 & 7
        if (w2 === 0) { const v = varint(); if (f2 === 1) h = v; else if (f2 === 2) m = v; else if (f2 === 3) s = v; else if (f2 === 4) ms = v }
        else skip(w2)
      }
      intervalSec = (h * 3600 + m * 60 + s + ms / 1000) || 1
    } else if (f === 2) {                           // heart_rate_samples
      if (w === 2) { const end = i + varint(); while (i < end) hr.push(varint()) }
      else if (w === 0) hr.push(varint())
      else skip(w)
    } else if (f === 28 && w === 2) {               // rr_samples
      const end = i + varint()
      while (i < end) {
        const t2 = varint(), f2 = t2 >> 3, w2 = t2 & 7
        if (f2 === 1 && w2 === 2) { const e2 = i + varint(); while (i < e2) rr.push(varint()) }
        else if (f2 === 1 && w2 === 0) rr.push(varint())
        else skip(w2)
      }
    } else skip(w)
  }
  const t0 = startMs ?? Date.now()
  if (rr.length) {                                  // RR: pulso + HRV reconstruibles
    let t = t0
    return rr.filter(v => v > 250 && v < 2500).map(v => { t += v; return { time: Math.round(t), hr: Math.round(60000 / v), rr: [v] } })
  }
  return hr.filter(v => v > 20 && v < 250).map((v, idx) => ({ time: Math.round(t0 + idx * intervalSec * 1000), hr: v }))
}

// ── Parse H10 exercise HR.REC file ───────────────────────────────────────────
function parseH10ExerciseFile(bytes, fileStartMs) {
  const samples = []

  // Strategy 1: exercise header — [year:2LE][mon:1][day:1][hr:1][min:1][sec:1][dur:4LE]…
  if (bytes.length > 12) {
    const year = bytes[0] | (bytes[1] << 8)
    if (year >= 2020 && year <= 2035) {
      const startMs = fileStartMs ?? Date.UTC(year, (bytes[2] || 1) - 1, bytes[3] || 1,
                                               bytes[4] || 0, bytes[5] || 0, bytes[6] || 0)
      // Samples start at byte 11 (some firmware) or 13 (others)
      let dataOff = 11
      if (bytes[11] < 20 || bytes[11] > 250) dataOff = 13
      for (let i = dataOff; i < bytes.length; i++) {
        const hr = bytes[i]
        if (hr > 20 && hr < 250) samples.push({ time: startMs + (i - dataOff) * 1000, hr })
        else if (samples.length > 10) break
      }
      if (samples.length >= 5) return samples
    }
  }

  // Strategy 2: longest valid HR run in the file
  let best = [], cur = []
  for (const b of bytes) {
    if (b > 20 && b < 250) { cur.push(b) }
    else { if (cur.length > best.length) best = cur; cur = [] }
  }
  if (cur.length > best.length) best = cur

  const t0 = fileStartMs ?? Date.now()
  return best.map((hr, i) => ({ time: t0 + i * 1000, hr }))
}

function parseTimeFolder(dateStr, timeStr) {
  const d = (dateStr ?? '').replace(/\/$/, '')
  const t = (timeStr ?? '').replace(/\/$/, '')
  if (d.length === 8 && t.length === 6) {
    return Date.UTC(
      parseInt(d.slice(0,4)), parseInt(d.slice(4,6))-1, parseInt(d.slice(6,8)),
      parseInt(t.slice(0,2)), parseInt(t.slice(2,4)), parseInt(t.slice(4,6))
    )
  }
  return null
}

// ── Traverse H10 directory tree ───────────────────────────────────────────────
async function findExerciseFiles(h2dCh, d2hCh, mtu, onStatus) {
  const hrPaths = []

  onStatus('Leyendo directorio del H10…')
  let dateEntries = []
  try {
    const bytes  = await psFtpGetFixed(h2dCh, d2hCh, '/U/0/', mtu)
    dateEntries  = parseProtoDirectory(bytes).filter(e => e.isDir)
  } catch (e) {
    throw new Error(
      `No se pudo leer el sistema de archivos del H10: ${e.message}. ` +
      'Asegurate de que el H10 esté emparejado con esta computadora (no solo con el celular) ' +
      'y que el firmware sea 3.x o superior.'
    )
  }

  for (const dateEntry of dateEntries.slice(-10).reverse()) {
    const dp = `/U/0/${dateEntry.name}`
    // H10: /U/0/YYYYMMDD/E/HHMMSS/  (E = Exercise) con SAMPLES.BPB (grabación
    // iniciada por SDK/VitalCore) o HR.REC (grabaciones viejas de Polar Beat)
    for (const sub of ['E/', 'R/']) {
      try {
        const bytes = await psFtpGetFixed(h2dCh, d2hCh, `${dp}${sub}`, mtu)
        const times = parseProtoDirectory(bytes).filter(e => e.isDir)
        for (const t of times) {
          hrPaths.push({
            dir: `${dp}${sub}${t.name}`,
            dateStr: dateEntry.name,
            timeStr: t.name,
          })
        }
      } catch { /* directory doesn't exist for this date */ }
    }
  }
  return hrPaths
}

// El H10 atiende UNA conexión a la vez: si otra app o pestaña lo tiene agarrado
// (una medición en vivo, el celular), gatt.connect() se cuelga SIN error — por
// eso el timeout con explicación, en vez de un "Hablando con el sensor…" eterno.
const SENSOR_BUSY_MSG =
  'El sensor no respondió. Casi siempre es porque otra conexión lo está usando: ' +
  'cerrá Manabi en el celular y cortá cualquier medición en vivo (✕ Desconectar) ' +
  'en otras pestañas — el H10 atiende a uno a la vez — y probá de nuevo.'
function connectWithTimeout(device, ms = 12000) {
  return Promise.race([
    device.gatt.connect(),
    new Promise((_, rej) => setTimeout(() => {
      try { device.gatt.disconnect() } catch { /* nunca llegó a abrir */ }
      rej(new Error(SENSOR_BUSY_MSG))
    }, ms)),
  ])
}

// ── Conexión PS-FTP efímera (Web Bluetooth): conectar → operar → desconectar ──
async function withPsFtp(run) {
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: [PSFTP_SERVICE],
  })
  const server = await connectWithTimeout(device)
  try {
    let psFtp
    try {
      psFtp = await server.getPrimaryService(PSFTP_SERVICE)
    } catch {
      throw new Error('El H10 no expone el servicio de archivos — actualizá su firmware desde Polar Beat (una sola vez)')
    }
    const mtuCh = await psFtp.getCharacteristic(PSFTP_MTU_CH)
    const d2hCh = await psFtp.getCharacteristic(PSFTP_D2H_CH)
    const h2dCh = await psFtp.getCharacteristic(PSFTP_H2D_CH)
    await d2hCh.startNotifications()
    const mtu = await waitMtu(mtuCh)
    await new Promise(r => setTimeout(r, 400))
    return await run({ h2dCh, d2hCh, mtu, deviceName: device.name })
  } finally {
    try { device.gatt.disconnect() } catch { /* ya cerrado */ }
  }
}

// ── Respaldo EN VIVO: mismas operaciones PS-FTP pero sobre la conexión GATT que
// ya tiene la sesión (usePolarH10), sin abrir otra. El vivo las usa para:
// arrancar una grabación interna de respaldo al conectar, y si hubo un corte
// (salir a correr), traer del sensor lo que no se transmitió y rellenar el hueco.
export const PSFTP_SERVICE_UUID = PSFTP_SERVICE

export async function openPsFtpOn(server) {
  const psFtp = await server.getPrimaryService(PSFTP_SERVICE)
  const mtuCh = await psFtp.getCharacteristic(PSFTP_MTU_CH)
  const d2hCh = await psFtp.getCharacteristic(PSFTP_D2H_CH)
  const h2dCh = await psFtp.getCharacteristic(PSFTP_H2D_CH)
  await d2hCh.startNotifications()
  const mtu = await waitMtu(mtuCh)
  await new Promise(r => setTimeout(r, 300))
  return { h2dCh, d2hCh, mtu }
}

export async function recStatusOn(ps) {
  return parseRecordingStatus(await psFtpQueryFixed(ps.h2dCh, ps.d2hCh, Q_RECORDING_STATUS, null, ps.mtu))
}
export async function recStartOn(ps, sampleType = SAMPLE_HR) {
  await psFtpQueryFixed(ps.h2dCh, ps.d2hCh, Q_START_RECORDING, encodeStartRecordingParams(sampleType), ps.mtu)
}
export async function recStopOn(ps) {
  await psFtpQueryFixed(ps.h2dCh, ps.d2hCh, Q_STOP_RECORDING, null, ps.mtu)
}

// Trae la grabación interna MÁS RECIENTE (la de respaldo que arrancó el vivo) y
// la convierte en lecturas ancladas en anchorMs — no se confía en el reloj del
// sensor (nunca lo seteamos): el ancla es el momento en que NOSOTROS la iniciamos.
export async function fetchLatestExerciseOn(ps, anchorMs) {
  const folders = await findExerciseFiles(ps.h2dCh, ps.d2hCh, ps.mtu, () => {})
  if (!folders.length) return []
  const latest = folders
    .map(f => ({ ...f, key: (f.dateStr || '') + (f.timeStr || '') }))
    .sort((a, b) => a.key.localeCompare(b.key))
    .pop()
  let bytes
  try { bytes = await psFtpGetFixed(ps.h2dCh, ps.d2hCh, `${latest.dir}SAMPLES.BPB`, ps.mtu) } catch { return [] }
  return parsePbExerciseSamples(bytes, anchorMs)
}

const isUserCancel = (e) =>
  e?.name === 'NotFoundError' || e?.name === 'AbortError' ||
  /cancel/i.test(e?.message || '')

// ── Hook ─────────────────────────────────────────────────────────────────────

export function usePolarOffline() {
  const [status,    setStatus]    = useState('idle')
  const [statusMsg, setStatusMsg] = useState('')
  const [hrSamples, setHrSamples] = useState([])
  const [progress,  setProgress]  = useState(0)
  const [error,     setError]     = useState(null)
  const [deviceName,setDeviceName]= useState(null)
  // Estado de la grabación interna del sensor: on = true/false/null (desconocido)
  const [rec, setRec] = useState({ busy: false, on: null, error: null })

  // ── Puente con el caparazón (la app nativa hace el BLE) ─────────────────────
  const pendingShell = useRef(new Map())
  useEffect(() => {
    if (!IN_NATIVE_SHELL) return
    const onNative = (e) => {
      const d = e?.detail
      if (d?.kind !== 'sensorrec') return
      if (d.op === 'fetch-progress') {
        setStatus(d.stage || 'downloading')
        if (d.msg) setStatusMsg(d.msg)
        if (d.pct != null) setProgress(d.pct)
        return
      }
      const waiter = pendingShell.current.get(d.op)
      if (waiter) { pendingShell.current.delete(d.op); waiter(d) }
    }
    window.addEventListener('vc-native', onNative)
    return () => window.removeEventListener('vc-native', onNative)
  }, [])
  const shellCmd = useCallback((op, extra = {}, timeoutMs = 45000) => new Promise((resolve) => {
    const t = setTimeout(() => {
      pendingShell.current.delete(op)
      resolve({ ok: false, error: 'El sensor no respondió — ¿está puesto y con Bluetooth encendido?' })
    }, timeoutMs)
    pendingShell.current.set(op, (d) => { clearTimeout(t); resolve(d) })
    try { window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'sensorrec', op, ...extra })) }
    catch { clearTimeout(t); pendingShell.current.delete(op); resolve({ ok: false, error: 'No se pudo hablar con la app' }) }
  }), [])

  // ── Comandos de grabación interna (VitalCore habla directo con el H10) ──────
  const startSensorRec = useCallback(async (kind = 'rr') => {
    const sampleType = kind === 'hr' ? SAMPLE_HR : SAMPLE_RR
    setRec(r => ({ ...r, busy: true, error: null }))
    try {
      if (IN_NATIVE_SHELL) {
        const d = await shellCmd('start', { sampleType })
        if (!d.ok) throw new Error(d.error || 'No se pudo iniciar la grabación')
      } else {
        await withPsFtp(({ h2dCh, d2hCh, mtu }) =>
          psFtpQueryFixed(h2dCh, d2hCh, Q_START_RECORDING, encodeStartRecordingParams(sampleType), mtu))
      }
      setRec({ busy: false, on: true, error: null })
      return true
    } catch (e) {
      setRec({ busy: false, on: null, error: isUserCancel(e) ? null : e.message })
      return false
    }
  }, [shellCmd])

  const stopSensorRec = useCallback(async () => {
    setRec(r => ({ ...r, busy: true, error: null }))
    try {
      if (IN_NATIVE_SHELL) {
        const d = await shellCmd('stop')
        if (!d.ok) throw new Error(d.error || 'No se pudo detener la grabación')
      } else {
        await withPsFtp(({ h2dCh, d2hCh, mtu }) =>
          psFtpQueryFixed(h2dCh, d2hCh, Q_STOP_RECORDING, null, mtu))
      }
      setRec({ busy: false, on: false, error: null })
      return true
    } catch (e) {
      setRec({ busy: false, on: null, error: isUserCancel(e) ? null : e.message })
      return false
    }
  }, [shellCmd])

  const checkSensorRec = useCallback(async ({ quiet = false } = {}) => {
    setRec(r => ({ ...r, busy: true, error: null }))
    try {
      let on
      if (IN_NATIVE_SHELL) {
        const d = await shellCmd('status')
        if (!d.ok) throw new Error(d.error || 'No se pudo consultar el sensor')
        on = !!d.recordingOn
      } else {
        const bytes = await withPsFtp(({ h2dCh, d2hCh, mtu }) =>
          psFtpQueryFixed(h2dCh, d2hCh, Q_RECORDING_STATUS, null, mtu))
        on = parseRecordingStatus(bytes).on
      }
      setRec({ busy: false, on, error: null })
      return on
    } catch (e) {
      // quiet: consulta de fondo (al abrir el panel) — si el sensor no está a
      // mano no ensuciamos la pantalla con el error del escaneo
      setRec({ busy: false, on: null, error: quiet || isUserCancel(e) ? null : e.message })
      return null
    }
  }, [shellCmd])

  const fetchRecording = useCallback(async () => {
    // Dentro de la app: el caparazón hace todo el BLE y devuelve las lecturas
    if (IN_NATIVE_SHELL) {
      setStatus('connecting'); setStatusMsg('Conectando con el H10…')
      setError(null); setHrSamples([]); setProgress(0)
      const d = await shellCmd('fetch', {}, 240000)
      if (!d.ok) {
        if (d.error === 'CANCELLED') { setStatus('idle'); return }
        setError(d.error || 'No se pudo traer la grabación'); setStatus('error'); return
      }
      if (!d.samples?.length) { setStatus('nosession'); return }
      if (d.deviceName) setDeviceName(d.deviceName)
      setHrSamples([...d.samples].sort((a, b) => a.time - b.time))
      setProgress(100); setStatusMsg(''); setStatus('done')
      return
    }

    setStatus('connecting')
    setStatusMsg('Buscando H10…')
    setError(null)
    setHrSamples([])
    setProgress(0)

    let device = null
    let usedGetDevices = false

    if (navigator.bluetooth && navigator.bluetooth.getDevices) {
      try {
        const permittedDevices = await navigator.bluetooth.getDevices()
        const polarDevice = permittedDevices.find(d => d.name && d.name.startsWith('Polar'))
        if (polarDevice) {
          console.log('[usePolarOffline] Se encontró un dispositivo Polar previamente vinculado:', polarDevice.name)
          device = polarDevice
          usedGetDevices = true
        }
      } catch (getDevicesErr) {
        console.warn('[usePolarOffline] Error obteniendo dispositivos vinculados:', getDevicesErr)
      }
    }

    try {
      if (!device) {
        device = await navigator.bluetooth.requestDevice({
          acceptAllDevices: true,
          optionalServices: [PSFTP_SERVICE],
        })
      }
      setDeviceName(device.name ?? 'Polar H10')
      setStatusMsg('Conectando…')

      let server
      try {
        if (device.gatt.connected) {
          try { device.gatt.disconnect() } catch {}
          await new Promise(r => setTimeout(r, 300))
        }

        server = await connectWithTimeout(device, 8000)
      } catch (connectErr) {
        if (usedGetDevices) {
          console.log('[usePolarOffline] Falló la conexión directa (posible sensor apagado o estado zombie). Fallback al selector...', connectErr.message)
          
          device = await navigator.bluetooth.requestDevice({
            acceptAllDevices: true,
            optionalServices: [PSFTP_SERVICE],
          })
          setDeviceName(device.name ?? 'Polar H10')
          setStatusMsg('Conectando…')

          if (device.gatt.connected) {
            try { device.gatt.disconnect() } catch {}
            await new Promise(r => setTimeout(r, 300))
          }

          server = await connectWithTimeout(device, 12000)
        } else {
          throw connectErr
        }
      }
      let psFtp
      try {
        psFtp = await server.getPrimaryService(PSFTP_SERVICE)
      } catch {
        throw new Error(
          'El H10 no expone el servicio de archivo (FEEE 0xFEEE). ' +
          'Verificá que el firmware sea versión 3.x o superior ' +
          '(actualizá desde Polar Beat → Perfil → Tu H10 → Actualizar firmware).'
        )
      }

      const mtuCh = await psFtp.getCharacteristic(PSFTP_MTU_CH)
      const d2hCh = await psFtp.getCharacteristic(PSFTP_D2H_CH)
      const h2dCh = await psFtp.getCharacteristic(PSFTP_H2D_CH)

      // ── Init sequence (required by Polar SDK) ──────────────────────────────
      // 1. Enable D2H notifications first (so we're ready to receive before writing)
      await d2hCh.startNotifications()
      // 2. Wait for device MTU notification — listener added BEFORE startNotifications
      //    inside waitMtu() to avoid the race condition
      setStatusMsg('Negociando MTU…')
      const mtu = await waitMtu(mtuCh)
      // 3. Brief settle delay so the BLE stack registers all notifications
      await new Promise(r => setTimeout(r, 400))

      // Si el sensor sigue grabando, se detiene primero: el archivo en curso
      // no se puede leer completo hasta cerrarlo. (Espejo del lado nativo.)
      try {
        setStatusMsg('Consultando el sensor…')
        const st = parseRecordingStatus(await psFtpQueryFixed(h2dCh, d2hCh, Q_RECORDING_STATUS, null, mtu))
        if (st.on) { setStatusMsg('Deteniendo la grabación…'); await psFtpQueryFixed(h2dCh, d2hCh, Q_STOP_RECORDING, null, mtu) }
      } catch { /* sin estado: se intenta leer igual */ }

      // ── Scan exercise file tree ────────────────────────────────────────────
      setStatus('scanning')
      const files = await findExerciseFiles(
        h2dCh, d2hCh, mtu,
        msg => setStatusMsg(msg)
      )

      if (!files.length) {
        await d2hCh.stopNotifications().catch(() => {})
        await device.gatt.disconnect().catch(() => {})
        setStatus('nosession')
        return
      }

      // ── Download each HR.REC ───────────────────────────────────────────────
      setStatus('downloading')
      let allSamples = []

      for (let i = 0; i < files.length; i++) {
        const { dir, dateStr, timeStr } = files[i]
        setProgress(Math.round((i / files.length) * 90))
        setStatusMsg(`Descargando ${i + 1}/${files.length}…`)
        const startMs = parseTimeFolder(dateStr, timeStr)

        // Primero el formato nuevo (SAMPLES.BPB, protobuf con RR/HRV); si esa
        // carpeta no lo tiene, el viejo HR.REC de Polar Beat.
        let parsed = []
        try {
          const bytes = await psFtpGetFixed(h2dCh, d2hCh, `${dir}SAMPLES.BPB`, mtu)
          parsed = parsePbExerciseSamples(bytes, startMs)
        } catch { /* no hay SAMPLES.BPB acá */ }
        if (!parsed.length) {
          try {
            const bytes = await psFtpGetFixed(h2dCh, d2hCh, `${dir}HR.REC`, mtu)
            parsed = parseH10ExerciseFile(bytes, startMs)
          } catch (e) { console.warn('Skip', dir, e.message) }
        }
        allSamples = allSamples.concat(parsed)
      }

      await d2hCh.stopNotifications().catch(() => {})
      await device.gatt.disconnect().catch(() => {})

      const final = allSamples.sort((a, b) => a.time - b.time)
      if (!final.length) {
        throw new Error(
          'El H10 envió los archivos pero no se pudieron leer datos de FC. ' +
          'Asegurate de que la sesión en Polar Beat esté finalizada (no solo pausada).'
        )
      }

      setHrSamples(final)
      setProgress(100)
      setStatusMsg('')
      setStatus('done')

    } catch (err) {
      if (err.name === 'NotFoundError' || err.name === 'AbortError' ||
          err.message?.toLowerCase().includes('cancel') ||
          err.message?.toLowerCase().includes('user cancelled')) {
        setStatus('idle')
      } else {
        setError(err.message)
        setStatus('error')
      }
    }
  }, [])

  const reset = useCallback(() => {
    setStatus('idle'); setStatusMsg(''); setError(null)
    setHrSamples([]); setProgress(0)
  }, [])

  return { status, statusMsg, hrSamples, progress, error, deviceName, fetchRecording, reset, rec, startSensorRec, stopSensorRec, checkSensorRec }
}
